import Styles from './PreviewRenderer.module.scss'
import { Spinner } from 'common/Spinner/Spinner'
import { Preview } from 'components/PostPreviewModal/Preview'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { PostPreviewChannelType } from 'components/PostPreviewModal/Types'
import { useEffect, useState } from 'react'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import {
  channelTypeFromProvider,
  providerFromChannelType,
} from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { PreviewAccountSelect } from '../PreviewAccountSelect/PreviewAccountSelect'
import { observer } from 'mobx-react-lite'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { StalePreviewOverlay } from 'components/PostPreviewModal/components/StalePreviewOverlay'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { generatePath, useHistory } from 'react-router-dom'
import { ScheduledFlowMode } from 'components/PostPreviewModalV2/PostPreviewStore'
import { useModals } from 'almanac-ui/Modal'
import { DESIGN_PATH } from 'paths'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'

interface Props {
  channel: PostPreviewChannelType
  pollPreviewContentStream: boolean
}

const createPlaceholderIntegration = (provider: ExternalIntegrationEnum): ExternalIntegration => ({
  id: -1 * (Object.values(ExternalIntegrationEnum).indexOf(provider) + 1),
  provider,
  provider_id: '1',
  name: `${ProviderToName[provider]} Account`,
  picture: '',
  account_type: 'profile',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  scopes: [],
  last_heartbeat_checked_at: new Date().toISOString(),
  last_disconnected_email_sent_at: null,
  disconnected_email_count: 0,
  is_active: true,
  type: 'ExternalIntegration',
})

export const PreviewRenderer = observer(({ channel, pollPreviewContentStream }: Props) => {
  const store = usePostPreviewStore()
  const { scheduledPost, isLoading, previewIsStale, fileId } = usePostPreviewContext()

  const { data: brandKitData } = useBrandKit(store.selectedBrandKitId)
  const history = useHistory()
  const { closeAllModals } = useModals()

  const accountsWithPlaceholder: ExternalIntegration[] = (() => {
    const accounts = Array.from(store.selectedAccounts)

    if (store.channel) {
      const channelProvider = providerFromChannelType[store.channel] as ExternalIntegrationEnum
      const hasAccountForChannel = store.selectedAccounts.some((acc) => acc.provider === channelProvider)
      const hasBrandKitIntegration = brandKitData?.brand_kit?.external_integrations.some(
        (integration) => integration.provider === channelProvider,
      )

      if (!hasAccountForChannel && !hasBrandKitIntegration) {
        accounts.unshift(createPlaceholderIntegration(channelProvider))
      }
    }

    store.selectedChannels.forEach((provider) => {
      if (store.channel && provider === providerFromChannelType[store.channel]) return

      const hasBrandKitIntegration = brandKitData?.brand_kit?.external_integrations.some(
        (integration) => integration.provider === provider,
      )

      if (!hasBrandKitIntegration) {
        accounts.push(createPlaceholderIntegration(provider))
      }
    })

    return accounts
  })()

  const [selected, setSelected] = useState<ExternalIntegration>(() => {
    if (store.channel) {
      const provider = providerFromChannelType[store.channel] as ExternalIntegrationEnum
      const brandIntegration =
        brandKitData?.brand_kit?.external_integrations.find((i) => i.provider === provider) || null
      const userIntegration = store.selectedAccounts.find((i) => i.provider === provider) || null
      return brandIntegration ?? userIntegration ?? createPlaceholderIntegration(provider)
    }

    return accountsWithPlaceholder[0]
  })

  useEffect(() => {
    if (!store.channel) return

    const provider = providerFromChannelType[store.channel] as ExternalIntegrationEnum
    const brandIntegration = brandKitData?.brand_kit?.external_integrations.find((i) => i.provider === provider) || null

    if (brandIntegration && brandIntegration.id !== selected.id) {
      setSelected(brandIntegration)
    }
  }, [brandKitData, store.channel]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className={Styles.root} data-testid="preview-renderer-loading">
        <Spinner />
      </div>
    )
  }

  const renderChannel = channelTypeFromProvider[selected.provider] as PostPreviewChannelType

  const firstListProvider = accountsWithPlaceholder[0]?.provider
  const canEdit = store.flow === ScheduledFlowMode.WIZARD && selected.provider === firstListProvider

  const handleEdit = () => {
    closeAllModals()
    history.push(generatePath(DESIGN_PATH, { key: fileId }))
  }

  return (
    <div className={Styles.root}>
      <div className={Styles.previewContainer}>
        <Preview
          channel={renderChannel}
          onEdit={canEdit ? handleEdit : undefined}
          loading={isLoading}
          post={scheduledPost}
          integration={selected}
          pollPreviewContentStream={pollPreviewContentStream}
        />

        {previewIsStale && (
          <div className={Styles.stalePreviewOverlay}>
            <StalePreviewOverlay />
          </div>
        )}
      </div>

      {!previewIsStale && store.selectedAccounts.length > 0 && (
        <PreviewAccountSelect
          externalIntegrations={accountsWithPlaceholder}
          selectedAccount={selected}
          onAccountSelect={setSelected}
          variant={store.target === 'text' ? 'avatar' : 'default'}
          image={scheduledPost?.media_objects?.[0]?.thumbnail_url || scheduledPost?.media_objects?.[0]?.source_url}
        />
      )}
    </div>
  )
})
